import { UnidadCotizacionServicio } from "@/constants/enums";
import { ServiceDetail } from "@/services/service-master.service";
import { formatCurrencyInputToNumber, isEmpty } from "@/utils/query-string";
import { AxiosError } from "axios";

// Errores 400 (regla de negocio: título duplicado, rangos de dosis
// solapados, solicitudes asociadas pendientes/realizadas) ya traen el copy
// exacto que pide la US en response.data.message, así que se muestra tal
// cual. Para cualquier otro status (500, etc.) se usa el copy hardcodeado
// del front, sin depender del texto que devuelva el back.
export const getServiceMasterErrorMessage = (
  error: unknown,
  fallbackMessage: string,
): string => {
  const errorObj = error as AxiosError<{ message?: string }>;
  const backendMessage = errorObj?.response?.data?.message;

  if (
    errorObj?.response?.status === 400 &&
    typeof backendMessage === "string" &&
    backendMessage.trim().length
  ) {
    return backendMessage;
  }

  return fallbackMessage;
};

export const getSelectedValue = (value: unknown): string => {
  const selected = Array.isArray(value) ? value[0] : value;
  return selected !== undefined && selected !== null ? String(selected) : "";
};

export const isDosisUnidad = (unidadCotizacion: unknown) =>
  Number(getSelectedValue(unidadCotizacion)) === UnidadCotizacionServicio.Dosis;

export const parseDecimalInput = (value: unknown): number | undefined => {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) return undefined;

  const parsed = Number(raw.replace(",", "."));
  return Number.isFinite(parsed) ? parsed : undefined;
};

interface DosisRangeLike {
  modalidad?: unknown;
  dosisDesde?: unknown;
  dosisHasta?: unknown;
  valorUnitario?: unknown;
}

interface ServiceMasterFormValuesLike {
  titulo?: unknown;
  categoria?: unknown;
  unidadCotizacion?: unknown;
  valorUnitario?: unknown;
  activo?: unknown;
  cotizacionesDosis?: DosisRangeLike[];
}

export const hasEmptyServiceMasterFormValues = (
  values: ServiceMasterFormValuesLike | undefined,
  requireActivo = false,
) => {
  const hasTitulo = Boolean(String(values?.titulo ?? "").trim());
  const hasCategoria = Boolean(getSelectedValue(values?.categoria));
  const hasUnidadCotizacion = Boolean(getSelectedValue(values?.unidadCotizacion));

  if (!hasTitulo || !hasCategoria || !hasUnidadCotizacion) return true;

  if (requireActivo) {
    const activo = Array.isArray(values?.activo) ? values?.activo?.[0] : values?.activo;
    if (typeof activo !== "boolean") return true;
  }

  if (isDosisUnidad(values?.unidadCotizacion)) {
    const rows: DosisRangeLike[] = values?.cotizacionesDosis ?? [];
    if (!rows.length) return true;

    const hasIncompleteRow = rows.some((row) => {
      const hasModalidad = Boolean(getSelectedValue(row?.modalidad));
      const hasDesde = !isEmpty(row?.dosisDesde);
      const hasHasta = !isEmpty(row?.dosisHasta);
      const hasValorUnitario = Boolean(
        formatCurrencyInputToNumber((row?.valorUnitario as string | number) ?? ""),
      );
      return !hasModalidad || !hasDesde || !hasHasta || !hasValorUnitario;
    });
    if (hasIncompleteRow) return true;

    return Boolean(getDosisRangesError(rows));
  }

  return !formatCurrencyInputToNumber(
    (values?.valorUnitario as string | number) ?? "",
  );
};

export const hasOverlappingDosisRanges = (
  rows: { modalidad?: string; desde?: number; hasta?: number }[],
) => {
  const byModalidad = new Map<string, { desde: number; hasta: number }[]>();

  rows.forEach((row) => {
    if (!row.modalidad || row.desde === undefined || row.hasta === undefined) {
      return;
    }
    const list = byModalidad.get(row.modalidad) ?? [];
    list.push({ desde: row.desde, hasta: row.hasta });
    byModalidad.set(row.modalidad, list);
  });

  for (const ranges of byModalidad.values()) {
    const sorted = [...ranges].sort((a, b) => a.desde - b.desde);
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i].desde <= sorted[i - 1].hasta) return true;
    }
  }

  return false;
};

export const getDosisRangesError = (
  rows: DosisRangeLike[] | undefined,
): string | null => {
  const list = rows ?? [];
  if (!list.length) return null;

  const parsedRows = list.map((row) => ({
    modalidad: getSelectedValue(row?.modalidad),
    desde: parseDecimalInput(row?.dosisDesde),
    hasta: parseDecimalInput(row?.dosisHasta),
  }));

  const hasInvalidOrder = parsedRows.some(
    (row) =>
      row.desde !== undefined && row.hasta !== undefined && row.desde >= row.hasta,
  );
  if (hasInvalidOrder) {
    return "El valor 'Desde' debe ser menor que el valor 'Hasta' en todos los rangos.";
  }

  if (hasOverlappingDosisRanges(parsedRows)) {
    return "Los rangos de la misma modalidad no pueden superponerse.";
  }

  return null;
};

export interface DosisRangesFeedback {
  message: string;
  variant: "warning" | "error";
}


// Modalidad + Desde + Hasta. Los rangos incompletos en esos 3 campos se
// ignoran (no se puede juzgar un rango a medias) y se señalan aparte para que
// el usuario siempre tenga una razón visible por la que "Agregar" está deshabilitado.
export const getDosisRangesFeedback = (
  rows: DosisRangeLike[] | undefined,
): DosisRangesFeedback | null => {
  const list = rows ?? [];

  if (!list.length) {
    return {
      variant: "warning",
      message: "Debe definir al menos un rango de dosis.",
    };
  }

  const hasIncompleteRangeFields = list.some((row) => {
    const hasModalidad = Boolean(getSelectedValue(row?.modalidad));
    const hasDesde = !isEmpty(row?.dosisDesde);
    const hasHasta = !isEmpty(row?.dosisHasta);
    return !hasModalidad || !hasDesde || !hasHasta;
  });

  if (hasIncompleteRangeFields) {
    return {
      variant: "warning",
      message: "Completá la modalidad y el rango de dosis (Desde/Hasta) de cada fila.",
    };
  }

  const error = getDosisRangesError(list);
  return error ? { variant: "error", message: error } : null;
};

const formatDecimalForInput = (value: number | undefined): string =>
  value !== undefined && value !== null ? String(value).replace(".", ",") : "";

export const mapServiceDetailToFormValues = (detail: ServiceDetail) => ({
  titulo: detail.titulo ?? "",
  valorUnitario:
    detail.valorUnitario !== undefined ? String(detail.valorUnitario) : "",
  // Los items de estos selects usan el valor numérico del enum tal cual
  // (ver categoriaOptions/unidadCotizacionOptions/modalidadOptions), así que
  // acá hay que preservar el número — si se convierte a string no matchea
  // ningún item y el select queda en "Seleccionar".
  categoria: detail.categoria !== undefined ? [detail.categoria] : [],
  unidadCotizacion:
    detail.unidadCotizacion !== undefined ? [detail.unidadCotizacion] : [],
  descripcion: detail.descripcion ?? "",
  activo: [Boolean(detail.activo)],
  cotizacionesDosis: (detail.cotizacionesDosis ?? []).length
    ? detail.cotizacionesDosis.map((row) => ({
        id: row.id,
        modalidad: row.modalidad !== undefined ? [row.modalidad] : [],
        dosisDesde: formatDecimalForInput(row.dosisDesde),
        dosisHasta: formatDecimalForInput(row.dosisHasta),
        valorUnitario:
          row.valorUnitario !== undefined ? String(row.valorUnitario) : "",
      }))
    : [{ modalidad: [], dosisDesde: "", dosisHasta: "", valorUnitario: "" }],
});
